import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiInfo } from "react-icons/fi";
import { BASE_URL } from "../../../../../BaseUrl";
import { getCookie } from "../../../../../functions/Functions";
import "./FinanceDashboard.css";

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "Rs 0";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 10000000) {
    return `${sign}Rs ${(absNum / 10000000).toFixed(2)} Cr`;
  }
  if (absNum >= 100000) {
    return `${sign}Rs ${(absNum / 100000).toFixed(2)} L`;
  }
  return `${sign}Rs ${absNum.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatFullCurrency = (val) => {
  if (val === null || val === undefined) return "Rs 0.00";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  return `${sign}Rs ${absNum.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const CashPositionWidget = ({ startDate, endDate, branch, initialData }) => {
  const [data, setData] = useState(initialData || null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    const fromDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().subtract(30, "days").format("YYYY-MM-DD");
    const toDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    const payload = { fromDate, toDate };
    if (branch) {
      if (Array.isArray(branch) && branch.length > 0) {
        payload.branch_id = branch.map((b) => b.id);
      } else if (branch?.id) {
        payload.branch_id = branch.id;
      }
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/widgets/accounting-cash-position`,
        payload,
        {
          headers: { Authorization: `Bearer ${getCookie()}` },
        }
      );
      if (response?.data?.data) {
        setData(response.data.data);
      } else if (response?.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching accounting cash position:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, branch]);

  const asOfDate = data?.as_of_date || null;
  const netLiquidPosition = Number(data?.net_liquid_position ?? 0);

  const cashObj = data?.cash || {};
  const totalCashBalance = Number(cashObj.total_cash_balance ?? 0);
  const cashAccountsCount = Array.isArray(cashObj.accounts)
    ? cashObj.accounts.length
    : 0;

  const bankObj = data?.bank || {};
  const totalBankBalance = Number(bankObj.total_bank_balance ?? 0);
  const bankAccountsCount = Array.isArray(bankObj.accounts)
    ? bankObj.accounts.length
    : 0;

  const totalAccountsCount = cashAccountsCount + bankAccountsCount;

  return (
    <div className="cash-position-card">
      {/* Header */}
      <div className="cash-position-header">
        <h5 className="cash-position-title m-0">Cash Position</h5>
        <span className="cash-position-subtitle">
          {asOfDate ? `As of ${asOfDate}` : "Across bank & cash"}
        </span>
      </div>

      {/* 2x2 Grid for 4 cards */}
      {isLoading ? (
        <div className="cash-position-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="cash-position-item">
              <div
                className="finance-skeleton-box mb-2"
                style={{ width: "55%", height: "13px" }}
              />
              <div
                className="finance-skeleton-box mb-2"
                style={{ width: "70%", height: "22px" }}
              />
              <div
                className="finance-skeleton-box"
                style={{ width: "40%", height: "12px" }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="cash-position-grid">
          {/* Card 1: Net Liquid Position */}
          <div className="cash-position-item">
            <span className="cash-position-item-title">
              Net Liquid Position
            </span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="net-liquid-tooltip">
                  {formatFullCurrency(netLiquidPosition)}
                </Tooltip>
              }
            >
              <span
                className={`cash-position-item-val ${
                  netLiquidPosition >= 0 ? "val-green" : "val-red"
                }`}
              >
                {formatCurrency(netLiquidPosition)}
              </span>
            </OverlayTrigger>
            <span className="cash-position-item-sub sub-gray">
              Cash + Bank balances
            </span>
          </div>

          {/* Card 2: Total Cash Balance */}
          <div className="cash-position-item">
            <span className="cash-position-item-title">Total Cash Balance</span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="cash-balance-tooltip">
                  {formatFullCurrency(totalCashBalance)}
                </Tooltip>
              }
            >
              <span
                className={`cash-position-item-val ${
                  totalCashBalance >= 0 ? "val-green" : "val-red"
                }`}
              >
                {formatCurrency(totalCashBalance)}
              </span>
            </OverlayTrigger>
            <span className="cash-position-item-sub sub-green">
              {cashAccountsCount} cash account
              {cashAccountsCount !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Card 3: Total Bank Balance */}
          <div className="cash-position-item">
            <span className="cash-position-item-title">Total Bank Balance</span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="bank-balance-tooltip">
                  {formatFullCurrency(totalBankBalance)}
                </Tooltip>
              }
            >
              <span
                className={`cash-position-item-val ${
                  totalBankBalance >= 0 ? "val-green" : "val-red"
                }`}
              >
                {formatCurrency(totalBankBalance)}
              </span>
            </OverlayTrigger>
            <span
              className={`cash-position-item-sub ${
                totalBankBalance >= 0 ? "sub-green" : "sub-red"
              }`}
            >
              {bankAccountsCount} bank account
              {bankAccountsCount !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Card 4: Total Accounts */}
          <div className="cash-position-item">
            <span className="cash-position-item-title">Total Accounts</span>
            <span className="cash-position-item-val val-amber">
              {totalAccountsCount}
            </span>
            <span className="cash-position-item-sub sub-gray">
              {cashAccountsCount} Cash | {bankAccountsCount} Bank
            </span>
          </div>
        </div>
      )}

      {/* Footer Notice */}
      <div className="cash-position-footer">
        <FiInfo className="cash-position-footer-icon" />
        <span>
          Net liquid position combines total cash ({formatFullCurrency(
            totalCashBalance
          )}) and bank balances ({formatFullCurrency(totalBankBalance)}).
        </span>
      </div>
    </div>
  );
};

export default CashPositionWidget;
